/**
 * CSS 生成引擎
 * 将 Word Numbering Schema 转换为 Scoped CSS
 */

import type {
  SchemaInput,
  GeneratedCSS,
  ComputedLevelStyle,
  NumFormat,
} from './types';
import { computeLevelStyle } from './styleResolver';
import { twipsToPx, halfPointsToPx, lineSpacingToCSS } from '../utils/units';

// ============ 常量映射 ============

/**
 * Word numFmt 到 CSS list-style-type 的映射
 */
const NUM_FMT_TO_CSS: Record<NumFormat, string> = {
  decimal: 'decimal',
  lowerLetter: 'lower-alpha',
  upperLetter: 'upper-alpha',
  lowerRoman: 'lower-roman',
  upperRoman: 'upper-roman',
  chineseCountingThousand: 'cjk-ideographic',
  bullet: 'none',
};

// ============ 工具函数 ============

/**
 * 转义 CSS 字符串中的特殊字符
 */
function escapeCSS(str: string): string {
  return str.replace(/["\\]/g, '\\$&');
}

/**
 * 为字体名称添加引号（如果需要）
 */
function quoteFont(name: string): string {
  if (/[\s,'"()]/.test(name)) {
    return `"${name.replace(/"/g, '\\"')}"`;
  }
  return name;
}

/**
 * 生成 font-family CSS 值
 */
function generateFontFamily(font: {
  ascii?: string | null;
  eastAsia?: string | null;
  hAnsi?: string | null;
}): string {
  const families: string[] = [];

  if (font.ascii) families.push(quoteFont(font.ascii));
  if (font.eastAsia) families.push(quoteFont(font.eastAsia));
  if (font.hAnsi && font.hAnsi !== font.ascii) {
    families.push(quoteFont(font.hAnsi));
  }

  // 添加通用 fallback
  families.push('system-ui', 'sans-serif');

  return families.join(', ');
}

// ============ Content 生成 ============

/**
 * 将 pattern (lvlText) 转换为 CSS content 值
 * @param pattern - 模式字符串，如 "%1、" 或 "(%1)"
 * @param numFmt - 编号格式
 * @param abstractNumId - AbstractNum ID
 * @param level - 当前级别
 * @returns CSS content 属性值
 */
export function patternToContent(
  pattern: string,
  numFmt: NumFormat,
  abstractNumId: number,
  _level: number
): string {
  // 空 pattern 表示"无"类型，返回空字符串
  if (!pattern) {
    return '""';
  }

  // Bullet 类型直接返回 pattern 字符
  if (numFmt === 'bullet') {
    const bulletChar = pattern || '•';
    return `"${escapeCSS(bulletChar)}"`;
  }

  // 处理 %n 占位符
  const counterStyle = NUM_FMT_TO_CSS[numFmt] || 'decimal';
  const parts: string[] = [];

  let lastIndex = 0;
  const regex = /%(\d)/g;
  let match;

  while ((match = regex.exec(pattern)) !== null) {
    // 添加 %n 之前的文本
    if (match.index > lastIndex) {
      const text = pattern.slice(lastIndex, match.index);
      parts.push(`"${escapeCSS(text)}"`);
    }

    // %n 中的 n 是 1-based，转换为 0-based level
    const refLevel = parseInt(match[1], 10) - 1;
    parts.push(`counter(num-${abstractNumId}-lvl-${refLevel}, ${counterStyle})`);

    lastIndex = regex.lastIndex;
  }

  // 添加剩余文本
  if (lastIndex < pattern.length) {
    const text = pattern.slice(lastIndex);
    parts.push(`"${escapeCSS(text)}"`);
  }

  return parts.join(' ');
}

// ============ CSS 规则生成 ============

/**
 * 生成单个级别的 CSS 规则
 *
 * WYSIWYG 缩进模型（与 Word 一致）：
 * - schema.left = 文本开始位置（距离左边界）
 * - schema.hanging = 符号到文本的距离（悬挂缩进）
 * - 符号位置 = left - hanging
 *
 * CSS 实现（精确匹配 Word）：
 * - margin-left = symbolPosition（符号起始位置）
 * - padding-left = hanging（符号到文本的距离，::before 占据此空间）
 * - ::before 绝对定位在 left:0, width:hanging（在 padding 区域内）
 */
function generateLevelCSS(
  computed: ComputedLevelStyle,
  abstractNumId: number,
  scopeClass: string,
  maxLevel: number,
  allLevels: { level: number; start: number }[]
): string {
  const selector = `.${scopeClass} .level-${computed.level}`;
  const rules: string[] = [];

  // 判断是否为"无"类型（空 pattern）
  const isNoneType = !computed.pattern;

  // 计算缩进（与 Word 完全一致）
  // 文本位置 = left（文本距离左边界的距离）
  const textPositionPx = twipsToPx(computed.indent.left);
  // 悬挂缩进 = hanging（符号到文本的距离）
  const hangingPx = twipsToPx(computed.indent.hanging);
  // 符号位置 = left - hanging（符号距离左边界的距离）
  const symbolPositionPx = textPositionPx - hangingPx;

  if (isNoneType) {
    // 无标记时，文本从 left 位置开始（无悬挂）
    rules.push(`margin-left: ${Math.max(0, textPositionPx).toFixed(2)}px`);
  } else {
    // 有标记时：margin-left = 符号位置，padding-left = hanging（文本从 left 开始）
    rules.push(`margin-left: ${Math.max(0, symbolPositionPx).toFixed(2)}px`);
    rules.push(`padding-left: ${hangingPx.toFixed(2)}px`);
    rules.push(`position: relative`);
  }

  // 只有有标记时才需要计数器递增
  if (!isNoneType) {
    rules.push(`counter-increment: num-${abstractNumId}-lvl-${computed.level}`);
  }

  // 扁平结构中，每个级别出现时重置所有更深级别的 counter
  const childResets = allLevels
    .filter(lvl => lvl.level > computed.level && lvl.level <= maxLevel)
    .map(lvl => `num-${abstractNumId}-lvl-${lvl.level} ${lvl.start - 1}`)
    .join(' ');
  if (childResets) {
    rules.push(`counter-reset: ${childResets}`);
  }

  // 段落间距
  if (computed.spacing?.before) {
    const beforePx = twipsToPx(parseInt(computed.spacing.before, 10));
    rules.push(`margin-top: ${beforePx.toFixed(2)}px`);
  }
  if (computed.spacing?.after) {
    const afterPx = twipsToPx(parseInt(computed.spacing.after, 10));
    rules.push(`margin-bottom: ${afterPx.toFixed(2)}px`);
  }

  // 行高
  if (computed.spacing?.line) {
    const lineValue = parseInt(computed.spacing.line, 10);
    const lineHeight = lineSpacingToCSS(lineValue, computed.spacing.lineRule);
    if (typeof lineHeight === 'number') {
      rules.push(`line-height: ${lineHeight.toFixed(2)}`);
    } else {
      rules.push(`line-height: ${lineHeight}`);
    }
  }

  // 字体
  if (computed.font) {
    rules.push(`font-family: ${generateFontFamily(computed.font)}`);
  }

  // 字号
  if (computed.fontSize) {
    rules.push(`font-size: ${halfPointsToPx(computed.fontSize).toFixed(2)}px`);
  }

  // 加粗
  if (computed.bold) {
    rules.push('font-weight: 700');
  }

  // 斜体
  if (computed.italic) {
    rules.push('font-style: italic');
  }

  // 下划线
  if (computed.underline) {
    rules.push('text-decoration: underline');
  }

  // 生成 ::before 伪元素
  const content = patternToContent(
    computed.pattern,
    computed.numFmt,
    abstractNumId,
    computed.level
  );

  const mainRule = `
${selector} {
  ${rules.join(';\n  ')};
}`;

  // ::before 伪元素 - 仅在有标记时生成
  let beforeRule = '';
  if (!isNoneType) {
    // position:absolute + left:0 = padding box 左边界
    // padding-left 区域正好是 ::before 的空间（从 padding box 左边界到 content box 左边界）
    beforeRule = `
${selector}::before {
  content: ${content};
  position: absolute;
  left: 0;
  width: ${hangingPx.toFixed(2)}px;
  white-space: nowrap;
}`;
  } else {
    // 无标记时隐藏 ::before
    beforeRule = `
${selector}::before {
  content: none;
}`;
  }

  return mainRule + '\n' + beforeRule;
}

// ============ 主生成函数 ============

/**
 * 生成指定 AbstractNum 的 Scoped CSS
 * @param schema - 完整 Schema
 * @param abstractNumId - AbstractNum ID
 * @param maxLevel - 最大级别（默认 4，UI 只处理 Level 0-4）
 * @returns 生成的 CSS 及元信息
 */
export function generateScopedCSS(
  schema: SchemaInput,
  abstractNumId: number,
  maxLevel = 4
): GeneratedCSS {
  const abstractKey = `AbstractNum_${abstractNumId}`;
  const abstractNum = schema.numberingSchemas[abstractKey];

  if (!abstractNum) {
    throw new Error(`${abstractKey} not found in schema`);
  }

  const scopeClass = `list-num-${abstractNumId}`;
  const cssBlocks: string[] = [];
  const levelSelectors: string[] = [];

  // 1. 生成容器样式 + counter-reset
  const counterResets = abstractNum.levels
    .slice(0, maxLevel + 1)
    .filter((lvl) => lvl != null)
    .map((lvl) => `num-${abstractNumId}-lvl-${lvl.level} ${lvl.start - 1}`)
    .join(' ');

  cssBlocks.push(`
.${scopeClass} {
  list-style: none;
  margin: 0;
  padding: 0;
  counter-reset: ${counterResets};
}`);

  // 2. 逐级生成样式
  const allLevelInfo = abstractNum.levels
    .slice(0, maxLevel + 1)
    .filter(lvl => lvl != null)
    .map(lvl => ({ level: lvl.level, start: lvl.start }));

  for (let i = 0; i <= Math.min(maxLevel, abstractNum.levels.length - 1); i++) {
    const level = abstractNum.levels[i];
    if (!level) continue;
    const computed = computeLevelStyle(level, schema.paragraphStyles);
    const levelCSS = generateLevelCSS(computed, abstractNumId, scopeClass, maxLevel, allLevelInfo);

    cssBlocks.push(levelCSS);
    levelSelectors.push(`.${scopeClass} .level-${i}`);
  }

  // 3. 生成嵌套重置规则
  const nestedResets = abstractNum.levels
    .slice(1, maxLevel + 1)
    .filter((lvl) => lvl != null)
    .map((lvl) => `num-${abstractNumId}-lvl-${lvl.level} ${lvl.start - 1}`)
    .join(' ');

  if (nestedResets) {
    cssBlocks.push(`
/* 嵌套列表时重置子级计数器 */
.${scopeClass} ol,
.${scopeClass} ul {
  counter-reset: ${nestedResets};
  list-style: none;
  margin: 0;
  padding: 0;
}`);
  }

  return {
    css: cssBlocks.join('\n'),
    scopeClass,
    levelSelectors,
  };
}

/**
 * 生成所有 AbstractNum 的 CSS
 * @param schema - 完整 Schema
 * @param maxLevel - 最大级别
 * @returns 所有生成的 CSS 合集
 */
export function generateAllCSS(
  schema: SchemaInput,
  maxLevel = 4
): Map<number, GeneratedCSS> {
  const results = new Map<number, GeneratedCSS>();

  for (const [key] of Object.entries(schema.numberingSchemas)) {
    const match = key.match(/^AbstractNum_(\d+)$/);
    if (match) {
      const id = parseInt(match[1], 10);
      results.set(id, generateScopedCSS(schema, id, maxLevel));
    }
  }

  return results;
}

/**
 * 生成内联样式对象（用于 React style 属性）
 */
export function generateInlineStyles(
  computed: ComputedLevelStyle,
  _abstractNumId: number
): {
  container: React.CSSProperties;
  marker: React.CSSProperties;
  isNoneType: boolean;
} {
  // 判断是否为"无"类型
  const isNoneType = !computed.pattern;

  // WYSIWYG 缩进模型（与 Word 一致）
  const textPositionPx = twipsToPx(computed.indent.left);
  const hangingPx = twipsToPx(computed.indent.hanging);
  const symbolPositionPx = textPositionPx - hangingPx;

  const container: React.CSSProperties = {
    position: 'relative',
  };

  if (isNoneType) {
    // 无标记：文本从 left 位置开始
    container.marginLeft = `${Math.max(0, textPositionPx).toFixed(2)}px`;
  } else {
    // 有标记：margin = 符号位置，padding = hanging（文本从 left 开始）
    container.marginLeft = `${Math.max(0, symbolPositionPx).toFixed(2)}px`;
    container.paddingLeft = `${hangingPx.toFixed(2)}px`;
  }

  if (computed.spacing?.before) {
    container.marginTop = `${twipsToPx(parseInt(computed.spacing.before, 10)).toFixed(2)}px`;
  }
  if (computed.spacing?.after) {
    container.marginBottom = `${twipsToPx(parseInt(computed.spacing.after, 10)).toFixed(2)}px`;
  }
  if (computed.spacing?.line) {
    const lineValue = parseInt(computed.spacing.line, 10);
    const lineHeight = lineSpacingToCSS(lineValue, computed.spacing.lineRule);
    container.lineHeight = typeof lineHeight === 'number' ? lineHeight : lineHeight;
  }
  if (computed.font) {
    container.fontFamily = generateFontFamily(computed.font);
  }
  if (computed.fontSize) {
    container.fontSize = `${halfPointsToPx(computed.fontSize).toFixed(2)}px`;
  }
  if (computed.bold) {
    container.fontWeight = 700;
  }
  if (computed.italic) {
    container.fontStyle = 'italic';
  }

  // 标记样式 - 在 padding 区域内
  const marker: React.CSSProperties = isNoneType
    ? { display: 'none' }
    : {
        position: 'absolute',
        left: `-${hangingPx.toFixed(2)}px`,
        width: `${hangingPx.toFixed(2)}px`,
      };

  return { container, marker, isNoneType };
}
